// libs
import React, { memo, useState, useEffect } from 'react';
import { styled } from '@nykaa/ui-components';

// global components
import RippleIt from 'shared/components/RippleIt';

// hooks
import { useKeyboardClick } from 'shared/hooks/useKeyboardClick';

// helpers
import { generateNykaaUrl } from 'shared/helpers/utils/url';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import { useDomainConfig } from 'shared/domainConfig/context/context';

const ARIA_LABELS = {
  SHARE_PRODUCT: 'Share this product',
};

// analytics
import { onShareClick } from './analytics';

// icons
import { useStoreIcon, IconName } from 'shared/icons';
import { getStoreUrl } from 'shared/helpers/storeUrl';

const UTM_PARAMS =
  '?utm_source=PDPShareWEB&utm_medium=PDPWEB&utm_campaign=PDPreferralWEB&utm_id=PDPShareBtnWEB';

// styled components
const StyledRippleIt = styled(RippleIt)`
  border-radius: ${({ theme }) => theme.borders.radiusFull};
  background: ${({ theme }) => hexToRgb(theme.colors.surface, 0.4)};
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

// defs
interface IShareLinkProps {
  title: string;
  url: string;
  size?: string;
}

// once rendered on client dont wait to check for API support next time
let isShareSupported = false;

const ShareLink = ({ title, url, size }: IShareLinkProps) => {
  const [showShareIcon, setShowShareIcon] = useState(false);
  const { SHARE } = useDomainConfig();
  const ShareAndroid = useStoreIcon(IconName.SHARE_ANDROID);

  useEffect(() => {
    // @ts-ignore window is not defined
    if (window.navigator && window.navigator.share) {
      setShowShareIcon(true);
    }
  }, []);

  const generateNykaaUrlWithUtm = () => {
    return `${generateNykaaUrl(getStoreUrl(url))}${UTM_PARAMS}`;
  };

  const shareData = () => {
    onShareClick();
    // @ts-ignore window is not defined
    window.navigator.share({
      url: generateNykaaUrlWithUtm(),
      text: SHARE.TEXT,
      title: title,
    });
  };

  const keyboardProps = useKeyboardClick(shareData);

  if (showShareIcon === false && isShareSupported === false) {
    return null;
  }

  isShareSupported = true;

  return (
    <StyledRippleIt
      as="a"
      {...keyboardProps}
      aria-label={ARIA_LABELS.SHARE_PRODUCT}
      data-at="share-icon"
    >
      <ShareAndroid size={size} aria-hidden="true" />
    </StyledRippleIt>
  );
};

export default memo(ShareLink);
